import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { themeColor } from '../theme/tokens';

// Light / dark theme preference for the customer dashboard (FR-X9, DR-45, CB-16).
// Copy of website/src/shell/theme-context.tsx adapted to this surface — per surface by
// design (CB-D12), never imported across surfaces.
//
// The preference is a per-viewer convenience kept in localStorage (never a token).
// Light is the default; only an explicit stored 'dark' switches. The device colour
// scheme is never consulted. The pre-paint script in index.html applies the same rule
// before React mounts, so there is no flash of the wrong theme.

export type ThemePref = 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeContextValue {
  /** What the user chose. */
  pref: ThemePref;
  /** What is showing right now (always equal to pref). */
  resolved: ResolvedTheme;
  setPref: (pref: ThemePref) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

// Kept identical to the inline pre-paint script in index.html.
export const THEME_STORAGE_KEY = 'bd.user.theme';

export function readStoredPref(): ThemePref {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pref, setPrefState] = useState<ThemePref>(readStoredPref);
  const resolved: ResolvedTheme = pref;

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', pref);
    root.style.colorScheme = pref;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', themeColor[pref]);
  }, [pref]);

  const setPref = useCallback((next: ThemePref) => {
    setPrefState(next);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      /* storage blocked (private mode) — the choice lasts for this page view */
    }
  }, []);

  const value = useMemo<ThemeContextValue>(() => ({ pref, resolved, setPref }), [pref, resolved, setPref]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
